import { StrictMode } from "react";
import { createRoot, hydrateRoot } from "react-dom/client";
import { I18nextProvider } from "react-i18next";

import { App } from "./App";
import { CONFIG_ELEMENT_ID, DEFAULT_CONFIG, type SiteConfig } from "./config";
import { createI18n, type Dictionary } from "./i18n/config";
import { DEFAULT_LOCALE, isLocale, rememberLocale, type Locale } from "./i18n/languages";
import { isRoutePath, type RoutePath } from "./routes";
import "./styles/global.css";
import "./styles/site.css";
import "./styles/sections.css";
import "./styles/about.css";

/** The id the pre-render writes its embedded dictionary under. */
const DICTIONARY_ELEMENT_ID = "i18n-dictionary";

/**
 * The page carries its own dictionary, so hydration needs no network round-trip and the
 * bundle carries no language it is not showing. Twenty-four locales cost exactly as much as
 * two.
 */
async function loadDictionary(locale: Locale): Promise<Dictionary> {
  const embedded = document.getElementById(DICTIONARY_ELEMENT_ID)?.textContent;
  if (embedded) return JSON.parse(embedded) as Dictionary;

  // `vite dev` serves the raw shell with nothing embedded. The branch is behind the DEV flag
  // so the production build emits no per-locale chunks at all — otherwise every language
  // would leave a chunk on disk that no visitor ever fetches.
  if (import.meta.env.DEV) {
    const module = await import(`./i18n/locales/${locale}.json`);
    return module.default as Dictionary;
  }

  throw new Error(`the page carries no #${DICTIONARY_ELEMENT_ID} block to hydrate ${locale} from`);
}

/** Same channel as the dictionary: baked into the page, so hydration matches what was rendered. */
function readConfig(): SiteConfig {
  const embedded = document.getElementById(CONFIG_ELEMENT_ID)?.textContent;
  return embedded ? (JSON.parse(embedded) as SiteConfig) : DEFAULT_CONFIG;
}

/**
 * `/uk/privacy` -> `{ locale: "uk", path: "/privacy", fromUrl: true }`. Falls back rather than
 * throwing. `fromUrl` separates "the URL named this language" from "we guessed the default",
 * because only the first is a statement about what the visitor wants.
 */
function readLocation(): { locale: Locale; path: RoutePath; fromUrl: boolean } {
  const [, head = "", ...rest] = window.location.pathname.split("/");
  const fromUrl = isLocale(head);
  const locale = fromUrl ? head : DEFAULT_LOCALE;

  const tail = `/${rest.join("/")}`.replace(/\/$/, "");
  const path = tail === "" ? "/" : tail;

  return { locale, path: isRoutePath(path) ? path : "/", fromUrl };
}

const container = document.getElementById("root");
if (!container) throw new Error("#root is missing from the document");

const { locale, path, fromUrl } = readLocation();

// Landing on a language page is a choice too — a search result, a shared link, or the switcher
// on the page before. Recording it here is what makes "/" remember it next time, for everyone
// and not only for visitors who found the switcher. A guessed default is not recorded: that
// would freeze English onto anyone whose first URL was malformed.
if (fromUrl) rememberLocale(locale);

const dictionary = await loadDictionary(locale);

const tree = (
  <StrictMode>
    <I18nextProvider i18n={createI18n(locale, dictionary)}>
      <App locale={locale} path={path} config={readConfig()} />
    </I18nextProvider>
  </StrictMode>
);

// Pre-rendered pages arrive with markup to adopt; `vite dev` serves an empty shell.
if (container.hasChildNodes()) {
  hydrateRoot(container, tree);
} else {
  createRoot(container).render(tree);
}
